import React, { useEffect, useMemo, useRef, useState } from "react";
import { SVG } from "../../../../../constants/assetLists";
import TaskCard from "./TaskCard";
import { useOperationsContext } from "../../../../../context/OperationsContextProvider";
import AppMenu from "../../../../../components/common/AppMenu";
import { useAnnouncementTaskMutations } from "../../../../../hooks/useAnnouncementTaskMutations";
import AppTooltip from "../../../../../components/common/AppTooltip";
import FileBox from "./FileBox";
import FormBox from "./FormBox";
import ChecklistBox from "./ChecklistBox";
import useScreenSize from "../../../../../hooks/useScreenSize";
import moment from "moment";
import {
  getRoleTypeName,
  getRoleTypeIcon,
  isSuperAdmin,
  isAdmin,
  isCreatedByNonAdmin,
  isReservationAnnouncement,
} from "../../../../../utils";
import { ICONS } from "../../../../users/data";
import AppConfirmationDialog from "../../../../../components/common/AppConfirmationDialog";
import ReservationAnnouncementLabel from "./ReservationAnnouncementLabel";
import useLongPress from "../../../../../hooks/useLongPress";

type Props = {
  announcement: any;
  index: number;
};

const MENUITEMS = [
  {
    title: "משימה",
    value: "task",
    icon: SVG.addGrayIcon,
    permission: "update_announcement",
  },
  {
    title: "עריכה",
    value: "edit",
    icon: SVG.editGrayIcon,
    permission: "update_announcement",
  },
  {
    title: "מחיקה",
    value: "delete",
    icon: SVG.deleteGrayIcon,
    permission: "delete_announcement",
  },
];

export function highlightSearchMatch(text: string, search: string) {
  if (!search) return text;

  const regex = new RegExp(`(${search})`, "gi");
  const parts = text.split(regex);
  return parts.map((part, index) =>
    part.toLowerCase() === search.toLowerCase() ? (
      <mark key={index} className="bg-yellow-200 rounded">
        {part}
      </mark>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  );
}

function AnnouncementCard({ announcement }: Props) {
  const {
    expandAnnouncementIndex,
    setExpandAnnouncementIndex,
    setSelectedAnnouncementId,
    announcementSearchText,
    setOpenAddEditAnnouncementDialog,
    setSelectedAnnouncementForEdit,
    setAnnouncementModalMode,
    showDailyBriefScreen,
    showCalendarScreen,
    filterSidebarVariant,
    setShowAnnouncementViewModal,
    selectedAnnouncementIds,
    toggleAnnouncementSelection,
  } = useOperationsContext();
  const {
    deleteAnnouncementMutation,
    softDeleteAnnouncementMutation,
    updateAnnouncementMutation,
    toggleAnnouncementResponderMutation,
    readAnnouncementMutation,
  } = useAnnouncementTaskMutations();

  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    },
    [],
  );

  const isExpanded = announcement._id === expandAnnouncementIndex;
  const isRegulation = announcement.announcementType == "Regulation";
  const isReservation = isReservationAnnouncement(announcement);

  const isAlert = announcement?.announcementType == "Error";
  const isUrgent = announcement.isUrgent;

  const startDateUtc = announcement?.scheduling?.startDate; // ISO UTC string
  const msPerDay = 24 * 60 * 60 * 1000;
  // Normalize both dates to UTC midnight to avoid timezone drift
  const utcStartOfDay = (d: Date) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  const dueDate = startDateUtc ? new Date(startDateUtc) : null;
  const now = new Date();

  const daysLeft =
    dueDate == null
      ? null
      : Math.ceil((utcStartOfDay(dueDate) - utcStartOfDay(now)) / msPerDay);

  // YELLOW → 4 weeks to 1 week before startDate (28..8 days left)
  const isRegulationDue =
    announcement?.announcementType === "Regulation" &&
    daysLeft != null &&
    daysLeft <= 28 &&
    daysLeft > 7;

  // RED → 1 week before OR on/after due date (<= 7 days left, including overdue)
  const isRegulationOverDue =
    announcement?.announcementType === "Regulation" &&
    daysLeft != null &&
    daysLeft <= 7;

  const hasTasks = announcement?.tasks && announcement?.tasks.length > 0;
  const isAnnouncementDone = announcement?.isCompleted;
  const isAllTasksDone = hasTasks
    ? announcement?.tasks?.every((task: any) => task?.isCompleted) &&
      isAnnouncementDone
    : isAnnouncementDone;
  const isRead = announcement.read || isAllTasksDone;

  const annIcon = useMemo(() => {
    const isRegular = announcement.announcementType == "Regular";
    const isAlert = announcement.announcementType == "Error";
    const isTraining = announcement.announcementType == "Training";
    const isAdministration = announcement.announcementType == "Administration";
    const isRegulation = announcement.announcementType == "Regulation";
    const isNotice = announcement.announcementType == "Notice";
    const isQuick = announcement.announcementType == "Quick";
    const isReservation = announcement.announcementType == "Reservation";
    const isMenu = announcement.announcementType == "Menu";

    const isUrgent = announcement.isUrgent === true;

    if (isAlert) {
      return SVG.redTriangleIcon;
    } else if (isQuick) {
      return SVG.quickTypeIcon;
    } else if (isReservation) {
      return SVG.tableIcon;
    } else if (isMenu) {
      return SVG.menuTypeIcon;
    } else if (isRegular) {
      return isUrgent
        ? SVG.regularAnnouncementRedIcon
        : SVG.regularAnnouncementIcon;
    } else if (isTraining) {
      return isUrgent ? SVG.tutorialsActiveRedIcon : SVG.tutorialsActiveIcon;
    } else if (isAdministration) {
      return isUrgent ? SVG.settingRedIcon : SVG.settingBlackIcon;
    } else if (isRegulation) {
      return isUrgent ? SVG.administrationRedIcon : SVG.administrationSmallIcon;
    } else if (isNotice) {
      return isUrgent ? SVG.exclamationCircleIcon : SVG.exclamationCircleIcon;
    }
  }, [announcement]);

  const handleOpenViewModal = () => {
    setSelectedAnnouncementId(announcement._id);
    setShowAnnouncementViewModal(true);
  };
  const statusIcon = useMemo(() => {
    const isAllTasksDone = hasTasks
      ? announcement?.tasks?.every((task: any) => task?.isCompleted)
      : false;
    const isTaskDone = announcement?.tasks?.some(
      (task: any) => task.isCompleted,
    );
    const type = announcement?.announcementType;

    if (isAllTasksDone && isAnnouncementDone) return SVG.completedCheckboxIcon;

    if (!hasTasks && isAnnouncementDone) return SVG.completedCheckboxIcon;

    if (type === "Regulation" && isRegulationOverDue) {
      if (isTaskDone || isAnnouncementDone) return SVG.errorPartial;
      return SVG.redCheckboxIcon;
    }

    if (type === "Regulation" && isRegulationDue) {
      if (isTaskDone || isAnnouncementDone) return SVG.regulationPartial;
      return SVG.yellowCheckboxIcon;
    }

    if (type === "Regulation") {
      return SVG.regularCheckboxIcon;
    }

    if (type === "Error") {
      if (isTaskDone || isAnnouncementDone) return SVG.errorPartial;
      return SVG.redCheckboxIcon;
    }

    // If any task done, still unchecked until all done
    if (isTaskDone || isAnnouncementDone)
      return SVG.partiallyCompleteCheckboxIcon;

    return SVG.regularCheckboxIcon;
  }, [announcement]);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const storedUser = localStorage.getItem("user");
  const currentUserId = useMemo(() => {
    return storedUser ? JSON.parse(storedUser)?._id : null;
  }, [storedUser]);

  const hasResponded = useMemo(() => {
    if (!currentUserId || !announcement?.respondedUsers) return false;
    return announcement.respondedUsers.some(
      (r: any) => r?.user?._id === currentUserId,
    );
  }, [announcement?.respondedUsers, currentUserId]);

  const handleToggleResponder = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleAnnouncementResponderMutation.mutate(announcement._id);
  };

  const [showRespondersDropdown, setShowRespondersDropdown] = useState(false);
  const respondersTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleRespondersMouseEnter = () => {
    if (respondersTimeoutRef.current)
      clearTimeout(respondersTimeoutRef.current);
    setShowRespondersDropdown(true);
  };

  const handleRespondersMouseLeave = () => {
    respondersTimeoutRef.current = setTimeout(() => {
      setShowRespondersDropdown(false);
    }, 200);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExpand = () => {
    if (!announcement?.readStatus?.isRead) {
      readAnnouncementMutation.mutate({
        id: announcement?._id,
        payload: {
          userId: currentUserId,
        },
      });
    }

    if (isExpanded) setExpandAnnouncementIndex(null);
    else setExpandAnnouncementIndex(announcement._id);
  };

  const handleItemClick = (value: string) => {
    if (
      deleteAnnouncementMutation.isPending ||
      softDeleteAnnouncementMutation.isPending
    )
      return;

    switch (value) {
      case "task":
        // Open Add Task Modal
        setSelectedAnnouncementForEdit(announcement);
        setAnnouncementModalMode("add-task");
        setOpenAddEditAnnouncementDialog(true);
        break;

      case "edit":
        // Open Edit Announcement Modal
        setSelectedAnnouncementForEdit(announcement);
        setAnnouncementModalMode("edit");
        setOpenAddEditAnnouncementDialog(true);
        break;

      case "delete":
        setShowConfirmationDialog(true);
        break;
      default:
        break;
    }

    handleMenuClose();
  };
  const handleToggleAnnouncementBrief = () => {
    updateAnnouncementMutation.mutate({
      id: announcement._id,
      payload: { showInBrief: !announcement.presentInBrief },
    });
  };

  const handleUpdateAnnouncementStatus = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateAnnouncementMutation.mutate({
      id: announcement._id,
      payload: {
        isCompleted: !announcement.isCompleted, // 🔁 toggle
      },
    });
  };

  const handleCloseConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  const handleConfirmDelete = () => {
    setShowConfirmationDialog(false);
    if (announcement?.showInCalendar || announcement?.showInBrief) {
      softDeleteAnnouncementMutation.mutate({
        id: announcement._id,
        screen: "operations",
      });
    } else {
      deleteAnnouncementMutation.mutate(announcement._id);
    }
  };

  const createdByMe = announcement?.createdBy?._id === currentUserId;
  const hasSameRole =
    announcement?.createdBy?.roles?.[0]?._id ===
    JSON.parse(storedUser!)?.roles?.[0]?._id;
  const showAnnouncementDoneCTA = useMemo(() => {
    if (!announcement || announcement.announcementType === "Notice")
      return false;
    const isAssignedToMe = announcement?.assignee?.some(
      (a: { _id: string }) => a._id === currentUserId,
    );
    const superAdmin = isSuperAdmin();
    if (createdByMe || isAssignedToMe || superAdmin) return true;
    if (announcement?.enabledDeletion && hasSameRole) return true;
    return false;
  }, [announcement, currentUserId]);

  const screenSize = useScreenSize();
  const maxLen = useMemo(() => {
    const isSidebarNarrow =
      filterSidebarVariant === "hidden" || filterSidebarVariant === "compact";

    const noRightPanels = !showDailyBriefScreen && !showCalendarScreen;

    const oneRightPanelOpen =
      (showDailyBriefScreen && !showCalendarScreen) ||
      (!showDailyBriefScreen && showCalendarScreen);

    const bothRightPanelsOpen = showDailyBriefScreen && showCalendarScreen;

    // helper
    const byScreen = (xl: number, x2l: number, fallback = 32) => {
      if (screenSize === "2xl") return x2l;
      if (screenSize === "xl") return xl;
      return fallback;
    };

    // widest case: sidebar narrow + no right panels
    if (isSidebarNarrow && noRightPanels) {
      return byScreen(60, 100);
    }

    // still wide: sidebar narrow + exactly one right panel open
    if (isSidebarNarrow && oneRightPanelOpen) {
      return byScreen(50, 80);
    }

    // medium: sidebar narrow + both right panels open (still saves some width vs full sidebar)
    if (isSidebarNarrow && bothRightPanelsOpen) {
      return byScreen(40, 65);
    }

    // full sidebar cases:
    // medium-ish: full sidebar + no right panels
    if (!isSidebarNarrow && noRightPanels) {
      return byScreen(55, 90);
    }

    // tighter: full sidebar + one panel open
    if (!isSidebarNarrow && oneRightPanelOpen) {
      return byScreen(42, 70);
    }

    // tightest: full sidebar + both panels open
    return byScreen(32, 45);
  }, [
    filterSidebarVariant,
    showDailyBriefScreen,
    showCalendarScreen,
    screenSize,
  ]);

  const allowDelete = useMemo(() => {
    const superAdmin = isSuperAdmin();
    const adminCondition =
      isAdmin() && isCreatedByNonAdmin(announcement?.createdBy);

    if (superAdmin || createdByMe || adminCondition) return true;
    if (announcement?.enabledDeletion) return hasSameRole;

    return false;
  }, [announcement, createdByMe, hasSameRole]);

  const FILTERED_MENUITEMS = useMemo(() => {
    if (!allowDelete) {
      return MENUITEMS.filter((item) => item.value !== "delete");
    } else return MENUITEMS;
  }, [allowDelete]);

  /* selecting is the entry point of the multi delete, so it follows the same
     rule as the delete menu item */
  const handleAnnouncementLongPress = () => {
    if (!allowDelete) return;

    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

    toggleAnnouncementSelection(announcement._id);
  };

  const announcementLongPressHandlers = useLongPress<HTMLDivElement>(
    handleAnnouncementLongPress,
    { disabled: !allowDelete },
  );

  const isSelected = selectedAnnouncementIds.has(announcement._id);

  return (
    <>
      <div
        data-announcement-id={announcement._id}
        className={`w-full ${
          isExpanded ? "bg-[#F7F7F7]" : "border-b border-b-gray40"
        }`}
      >
        {/* Announcement - relative container for absolute menu positioning */}
        <div className="relative">
          {/* Tooltip wraps entire double-clickable area */}
          <AppTooltip
            title="לחיצה כפולה לצפייה"
            enterDelay={3000}
            enterNextDelay={3000}
          >
            <div
              {...announcementLongPressHandlers}
              onDoubleClick={() => {
                if (clickTimeoutRef.current) {
                  clearTimeout(clickTimeoutRef.current);
                  clickTimeoutRef.current = null;
                }

                handleOpenViewModal();
              }}
              className={`w-full px-3 py-3 flex flex-col gap-3 hover:z-10 hover:!bg-lightIcyBlue hover:shadow-bottom-sm transition-all duration-200 ${
                isExpanded && "border-b border-b-gray40"
              } ${!isExpanded ? (isRead ? "bg-gray98" : "bg-white") : ""} ${isSelected && "!bg-lightIcyBlue"}`}
            >
              <div className="flex flex-wrap justify-between items-center gap-2">
                <div className="flex flex-row gap-2 items-center">
                  {isSelected && (
                    <img
                      src={SVG.checkTickIcon}
                      alt="icon"
                      className="w-[28px] h-[28px] object-contain"
                    />
                  )}
                  <button
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (clickTimeoutRef.current) {
                        clearTimeout(clickTimeoutRef.current);
                      }

                      clickTimeoutRef.current = setTimeout(() => {
                        handleExpand();
                      }, 250); // delay to detect double click
                    }}
                  >
                    <img
                      src={isExpanded ? SVG.expandIcon : SVG.lessThanIcon}
                      alt="icon"
                      className="w-[12px] h-[12px] object-contain"
                    />

                    <div
                      className={`flex items-center gap-2 min-w-[333px] rounded-[8px] px-2 py-1 ${
                        isRegulationDue
                          ? "bg-brightYellow"
                          : isRegulationOverDue
                            ? "bg-lightRed"
                            : ""
                      }`}
                    >
                      {isReservation ? (
                        <ReservationAnnouncementLabel
                          announcement={announcement}
                          isAllTasksDone={isAllTasksDone}
                          annIcon={annIcon}
                        />
                      ) : (
                        <>
                          <div className="w-[20px] h-[20px]">
                            <img
                              src={annIcon}
                              alt="icon"
                              className="w-full h-full object-contain"
                            />
                          </div>

                          <AppTooltip
                            title={
                              announcement?.announcementTitle?.length > maxLen
                                ? announcement?.announcementTitle
                                : ""
                            }
                          >
                            <span
                              className={`text-lg ${
                                isRegulation && isUrgent
                                  ? "text-[#810023] "
                                  : isAlert || isUrgent
                                    ? "text-raspberryRed"
                                    : "text-black"
                              } ${isRead ? "font-medium" : isUrgent ? "font-bold" : "font-semibold"}
                    ${isAllTasksDone && "line-through"}`}
                            >
                              {highlightSearchMatch(
                                announcement?.announcementTitle?.length > maxLen
                                  ? announcement.announcementTitle.slice(
                                      0,
                                      maxLen,
                                    ) + "..."
                                  : (announcement?.announcementTitle ?? ""),
                                announcementSearchText,
                              )}
                            </span>
                          </AppTooltip>
                        </>
                      )}
                    </div>
                  </button>
                </div>

                <div className="flex items-center mr-auto gap-4">
                  <div className="flex items-center gap-2">
                    {announcement?.enableRespondentCount && (
                      <>
                        <div
                          className="relative"
                          onMouseEnter={handleRespondersMouseEnter}
                          onMouseLeave={handleRespondersMouseLeave}
                        >
                          <span className="text-grayText cursor-pointer">
                            מס' משיבים: {announcement?.respondedCount ?? 0}
                          </span>

                          {showRespondersDropdown &&
                            announcement?.respondedUsers?.length > 0 && (
                              <div className="absolute top-full right-0 mt-1 z-50 bg-white border-[1px] border-[#707070] shadow-lg p-[2px] min-w-[203px] max-h-[300px] overflow-y-auto">
                                {announcement.respondedUsers.map(
                                  (responder: any) => {
                                    const user = responder?.user;
                                    if (!user) return null;
                                    const bgColor =
                                      user?.roles?.[0]?.color || "#E0E0E0";
                                    const fullName =
                                      `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

                                    return (
                                      <div
                                        key={responder._id}
                                        className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded"
                                      >
                                        <div
                                          className="w-[32px] h-[32px] rounded-full flex items-center justify-center shrink-0"
                                          style={{ backgroundColor: bgColor }}
                                        >
                                          <span className="text-xl text-black">
                                            {fullName.charAt(0)}
                                          </span>
                                        </div>
                                        <div>
                                          <span>{fullName}</span>
                                          {user.roles?.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-1 mb-3">
                                              <span
                                                className="text-xs text-grayText px-2 py-0.5 rounded-full flex items-center gap-1"
                                                style={{
                                                  backgroundColor: bgColor,
                                                }}
                                              >
                                                <img
                                                  src={
                                                    ICONS.find(
                                                      (item) =>
                                                        item.id ===
                                                        (getRoleTypeIcon(
                                                          user?.roles?.[0]
                                                            ?.roleType,
                                                        ) ||
                                                          user?.roles?.[0]
                                                            ?.icon),
                                                    )?.icon
                                                  }
                                                  alt=""
                                                  className="h-3 w-3"
                                                />
                                                <span>
                                                  {getRoleTypeName(
                                                    user?.roles?.[0]?.roleType,
                                                  ) || ""}
                                                </span>
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            )}
                        </div>

                        <div className="w-[1px] h-[20px] bg-gray50" />
                      </>
                    )}

                    {announcement?.tasks?.length > 0 && (
                      <>
                        <span className="text-grayText">
                          {announcement.taskStatus}:
                          {announcement?.tasks?.length ?? 0}
                        </span>

                        <div className="w-[1px] h-[20px] bg-gray50" />
                      </>
                    )}

                    <span className="text-grayText">
                      {`${announcement?.createdBy?.firstName ? `${announcement?.createdBy?.firstName} ` : ""}${
                        announcement?.createdBy?.lastName
                          ? `${announcement?.createdBy?.lastName} `
                          : ""
                      }`}
                    </span>

                    <div className="w-[1px] h-[20px] bg-gray50" />

                    <span className="text-grayText">
                      {announcement.createdAt
                        ? moment(announcement.createdAt).format(
                            "HH:mm DD/MM/YYYY",
                          )
                        : ""}
                    </span>
                  </div>

                  {
                    <button
                      className="w-[24px] h-[24px]"
                      onClick={handleToggleResponder}
                    >
                      <img
                        src={
                          hasResponded
                            ? SVG.thumbsUpGreenIcon
                            : SVG.thumbsUpGrayIcon
                        }
                        alt="thumbs up"
                        className="w-full h-full object-contain"
                      />
                    </button>
                  }

                  {showAnnouncementDoneCTA && (
                    <button
                      className="w-[24px] h-[24px]"
                      onClick={handleUpdateAnnouncementStatus}
                    >
                      <img
                        src={statusIcon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </button>
                  )}

                  {/* Spacer to maintain layout where menu would be */}
                  <div className="w-[30px]" />
                </div>
              </div>

              {isExpanded && announcement?.checklists?.length > 0 && (
                <div
                  className="pr-5 flex flex-wrap items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {announcement?.checklists?.map((item: any, index: number) => (
                    <ChecklistBox key={index} list={item} />
                  ))}
                </div>
              )}

              {isExpanded && announcement?.files?.length > 0 && (
                <div
                  className="pr-5 flex flex-wrap items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {announcement?.files?.map((file: any, index: number) => (
                    <FileBox key={index} file={file} />
                  ))}
                </div>
              )}

              {isExpanded && announcement?.forms && announcement.forms._id && (
                <div
                  className="pr-5 flex flex-wrap items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FormBox form={announcement.forms} />
                </div>
              )}
            </div>
          </AppTooltip>

          {/* AppMenu positioned absolutely - outside tooltip */}
          <div className="absolute top-4 left-3">
            <AppMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onOpen={handleMenuOpen}
              onClose={handleMenuClose}
              items={FILTERED_MENUITEMS}
              showSwitch={true}
              switchLabel="הצגה בבריף"
              switchChecked={announcement.presentInBrief}
              onSwitchChange={handleToggleAnnouncementBrief}
              onItemClick={handleItemClick}
            />
          </div>
        </div>

        {/* Tasks */}
        {isExpanded && announcement?.tasks?.length > 0 && (
          <div className="tasks-section w-full flex flex-col gap-1 pr-8 py-3 pl-1">
            {announcement?.tasks.map((task: any, index: number) => {
              return (
                <TaskCard
                  key={index}
                  announcement={announcement}
                  task={task}
                  index={index}
                />
              );
            })}
          </div>
        )}
      </div>

      <AppConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmDelete}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle="האם למחוק את ההודעה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default AnnouncementCard;
